import { ItemDataType } from "rsuite/esm/MultiCascadeTree";
import { Dispatch, SetStateAction, useMemo } from "react";
import { SelectPicker } from "rsuite";
import { useAtomValue } from "jotai";
import { Button } from "reactstrap";
import _ from "underscore";

import { Layout, Prefill } from "../../../../types/GlobalInterfaces";
import { GlobalsTypes } from "../Globals";
import { modulesAtom } from "../../../../atoms/GlobalAtoms";
import { BasicSearch } from "../../CloudSettingsHome";
import AppPageHeader from "../../../../components/Headers/AppPageHeader";

interface CustomAppHeaderProps<T extends Prefill | Layout> {
  items: T[];
  mobileScreen?: boolean;
  selectedModule: string;
  setSelectedModule: Dispatch<SetStateAction<string>>;
  selectedLayoutType?: string | null;
  setLayoutType?: Dispatch<SetStateAction<string | null>>;
  setSearchWord: Dispatch<SetStateAction<string | null>>;
  setCreateDrawerOpen: Dispatch<SetStateAction<boolean>>;
  type: GlobalsTypes;
}

type TypeMap = {
  [key in GlobalsTypes]: { title: string; description: string };
};

const typeMap: TypeMap = {
  QuickCreates: {
    title: "Quick Creates",
    description: "The quick creates global to your cloud.",
  },
  Layouts: {
    title: "Layouts",
    description: "The layouts global to your cloud.",
  },
};

/**
 * Header for searching/sorting through Global Items
 * Generates module dropdown based on Global Items
 *
 * For Global Layouts, allows you to filter based on layout type
 */
export default function GlobalAppHeader<T extends Layout | Prefill>({
  items,
  mobileScreen,
  selectedModule,
  setSelectedModule,
  selectedLayoutType,
  setLayoutType,
  setSearchWord,
  type,
  setCreateDrawerOpen,
}: CustomAppHeaderProps<T>) {
  const mods = useAtomValue(modulesAtom);
  const modulesSet = new Set();

  const globalLayoutTypes: ItemDataType<string>[] = [
    { value: "", label: "All" },
    { value: "Table", label: "Table" },
    { value: "Cards", label: "Card" },
    { value: "Board", label: "Kanban" },
    { value: "Timeline", label: "Timeline" },
  ];

  const globalModules = useMemo(() => {
    const gModules: ItemDataType<string>[] = [
      { value: "", label: "All Data Sources" },
    ];

    if (
      !Array.isArray(mods) ||
      !mods.length ||
      !Array.isArray(items) ||
      !items.length
    )
      return gModules;

    for (const item of items) {
      const moduleAPIName = item.ModuleAPIName;
      if (modulesSet.has(moduleAPIName)) continue;

      const moduleSchema = _.findWhere(mods, { APIName: moduleAPIName });

      if (!moduleSchema) continue;

      modulesSet.add(moduleAPIName);

      gModules.push({
        value: moduleAPIName,
        label: moduleSchema.Name,
      });
    }

    return gModules;
  }, [items]);

  const typeData = typeMap[type];

  return (
    <AppPageHeader
      title={typeData?.title}
      size="sm"
      className="py-3"
      description={typeData?.description}
      customContent={
        <div
          className="d-flex justify-content-end align-items-center"
          style={{ flexWrap: "wrap" }}
        >
          {!!items.length && (
            <div
              className={`${
                mobileScreen ? "mt-3" : ""
              } d-flex align-items-center mr-3`}
            >
              <i
                className="mdi mdi-database mr-1"
                style={{
                  fontSize: 24,
                }}
              />

              {/* SelectPicker for Modules */}
              <SelectPicker
                data={globalModules}
                value={selectedModule}
                searchable={items.length > 5}
                cleanable={!!selectedModule}
                onChange={(moduleAPIName: string | null) => {
                  setSelectedModule(moduleAPIName || "");
                }}
                style={{ minWidth: 170 }}
              />
            </div>
          )}

          {/* SelectPicker for Layout types */}
          {type === "Layouts" && setLayoutType && !!items.length && (
            <SelectPicker
              data={globalLayoutTypes}
              value={selectedLayoutType}
              cleanable={!!selectedLayoutType}
              onChange={(type: string | null) => {
                setLayoutType(type);
              }}
              style={{ minWidth: 170 }}
              placeholder="Layout Type"
              searchable={false}
            />
          )}

          {!!items?.length && type !== "Layouts" && (
            <div
              className={`${mobileScreen ? "mt-3" : ""} mr-3`}
              style={{ minWidth: 230 }}
            >
              <BasicSearch
                setSearchValue={setSearchWord}
                placeholder="Search"
              />
            </div>
          )}

          <div className={`${mobileScreen ? "mt-3" : ""}`}>
            <Button
              className="ml-4"
              color="primary"
              onClick={() => {
                setCreateDrawerOpen(true);
              }}
            >
              Create
            </Button>
          </div>
        </div>
      }
    />
  );
}
